import { firestore, IUserContext, Subtitle1, userContext, useTheme } from "capsule"
import { maxWidth } from "capsule/features/Theme/dimensions"
import firebase from "firebase/compat/app"
import React, { useContext, useMemo } from "react"
import { useCollectionData } from "react-firebase-hooks/firestore"
import { useTranslation } from "react-i18next"
import { Linking, Platform, ScrollView, StyleSheet, Text, View } from "react-native"
import FastImage from "react-native-fast-image"

import { images } from "../../assets/images"
import { ElectronicLabel } from "../../common/ElectronicLabel"
import { collections } from "../../common/types"
import {
  CE_HEIGHT,
  CE_ICON_SIZE,
  CE_WIDTH,
  DoctupDistributionMarkets,
  PECAN_PARTNER,
} from "../../features/config/Constants"
import { AppUserData } from "../../features/models/UserData"

const AboutScreen = () => {
  const {
    colors: {
      black: { highEmphasis: black },
      white: { highEmphasis: white },
      primary,
    },
    dimensions: { spacing },
  } = useTheme()
  const { t } = useTranslation()

  const { userData } = useContext<IUserContext<AppUserData>>(userContext)
  const isPecan = userData?.partner === PECAN_PARTNER
  const userLocation =
    !userData?.distributionMarket ||
    !DoctupDistributionMarkets.includes(userData?.distributionMarket)
      ? "EU-Fr"
      : userData?.distributionMarket
  const baseQuery = firestore()
    .collection(collections.ELECTRONIC_LABEL)
    .where("currentVersion", "==", true)
    .where("distributionMarket", "==", userLocation)
  const query = isPecan ? baseQuery.where("partner", "==", PECAN_PARTNER) : baseQuery
  const [electronicLabel] = useCollectionData<ElectronicLabel>(
    query as unknown as firebase.firestore.Query<ElectronicLabel>,
  )
  const electronicLabelData = electronicLabel?.[0]
  const s = useMemo(
    () => ({
      view: [
        Platform.OS === "web" ? styles.view : null,
        {
          backgroundColor: white,
          padding: spacing * 2,
          flexGrow: 1,
        },
      ],
      screenStyle: [Platform.OS === "web" ? { width: maxWidth } : null],
      title: {
        paddingBottom: spacing / 2,
        color: black,
      },
      text: {
        color: black,
      },
      linkText: {
        color: primary,
      },
    }),
    [black, white, spacing, primary],
  )

  const renderLine = (image: number, children: React.ReactNode) => (
    <View style={styles.line}>
      <View style={styles.iconWrapper}>
        <FastImage source={image} style={styles.icon} resizeMode="contain" />
      </View>
      <View style={styles.textContainer}>{children}</View>
    </View>
  )

  return (
    <ScrollView contentContainerStyle={s.view}>
      {electronicLabelData ? (
        <View style={s.screenStyle}>
          <Subtitle1 style={[s.text, styles.title]}>{electronicLabelData.softwareName}</Subtitle1>
          <Subtitle1
            style={[s.text, styles.title]}
          >{`V${electronicLabelData.softwareVersion}`}</Subtitle1>
          <Subtitle1 style={[s.text, styles.underTitle]}>{t("common:software.title")}</Subtitle1>
          {renderLine(
            images.logo_manufacture_date as number,
            <Subtitle1 style={s.text}>{electronicLabelData.manufactureDate}</Subtitle1>,
          )}
          <View style={styles.line}>
            <View style={styles.iconWrapper}>
              <FastImage
                source={images.logo_medical_device as number}
                style={styles.icon}
                resizeMode="contain"
              />
            </View>
            <View style={[styles.textContainer, styles.ceTextContainer]}>
              <FastImage
                source={images.logo_ce as number}
                style={styles.ceLogo}
                resizeMode="contain"
              />
            </View>
          </View>
          {renderLine(
            images.logo_UDI as number,
            <>
              <Subtitle1 style={s.text}>{electronicLabelData.udiDeviceIdentifier}</Subtitle1>
              <Subtitle1 style={s.text}>{electronicLabelData.udiProductionIdentifier}</Subtitle1>
              <Subtitle1 style={s.text}>
                {electronicLabelData.udiVersionIdentifier}
                {electronicLabelData.softwareVersion}
              </Subtitle1>
            </>,
          )}
          {renderLine(
            images.logo_manufacturer as number,
            <>
              <Subtitle1 style={s.text}>{electronicLabelData.manufacturerName}</Subtitle1>
              <Subtitle1 style={s.text}>{electronicLabelData.manufacturerStreetAddress}</Subtitle1>
              <Subtitle1 style={s.text}>
                {electronicLabelData.manufacturerCityPostalCountry}
              </Subtitle1>
            </>,
          )}
          {userLocation !== "EU-Fr" ? (
            <View style={styles.border}>
              <Subtitle1 style={[s.title, styles.centerText]}>
                {userLocation === "IN"
                  ? t("common:electronicLabel.indiaSale")
                  : t("common:electronicLabel.singaporeSale")}
              </Subtitle1>
              {renderLine(
                userLocation === "IN"
                  ? (images.india_rep as number)
                  : (images.singapore_rep as number),
                <>
                  <Subtitle1 style={s.text}>{electronicLabelData.importerName}</Subtitle1>
                  <Subtitle1 style={s.text}>{electronicLabelData.importerStreetAddress}</Subtitle1>
                  <Subtitle1 style={s.text}>
                    {electronicLabelData.importerCityPostalCountry}
                  </Subtitle1>
                  <Subtitle1 style={s.text}>
                    {t("common:electronicLabel.importLicenceNumber")}{" "}
                    {electronicLabelData.importerLicenseNumber}
                  </Subtitle1>
                </>,
              )}
            </View>
          ) : null}
          {renderLine(
            images.logo_instruction as number,
            <>
              <Subtitle1 style={s.text}>{t("common:electronicLabel.read")}</Subtitle1>
              <Text
                style={[s.linkText, styles.link]}
                onPress={() => Linking.openURL(t("common:electronicLabel.instructionsUrl"))}
              >
                {t("common:electronicLabel.instructionsUrl")}
              </Text>
            </>,
          )}
        </View>
      ) : null}
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  view: {
    alignItems: "center",
  },
  title: {
    marginBottom: 4,
  },
  underTitle: {
    marginBottom: 20,
  },
  line: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 24,
  },
  iconWrapper: {
    width: CE_ICON_SIZE,
    height: CE_ICON_SIZE,
    marginRight: 16,
    alignItems: "flex-start",
    justifyContent: "flex-start",
  },
  icon: {
    width: CE_ICON_SIZE,
    height: CE_ICON_SIZE,
  },
  ceLogo: {
    width: CE_WIDTH,
    height: CE_HEIGHT,
  },
  ceTextContainer: {
    justifyContent: "center",
    alignItems: "flex-start",
  },
  textContainer: {
    flex: 1,
    justifyContent: "flex-start",
  },
  link: {
    textDecorationLine: "underline",
  },
  centerText: {
    textAlign: "center",
  },
  border: {
    borderWidth: 1,
    padding: 10,
    marginBottom: 20,
    borderRadius: 8,
  },
})

export default AboutScreen
